// SPDX-License-Identifier: AGPL-3.0-or-later

import {StatusSlate} from '@app/features/app/components/dialogs/shared/StatusSlate';
import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import styles from '@app/features/cosmetics/components/CosmeticsSettingsTab.module.css';
import {RoomOutfitEditor} from '@app/features/cosmetics/components/RoomOutfitEditor';
import editorStyles from '@app/features/cosmetics/components/RoomOutfitEditor.module.css';
import Cosmetics, {
	type CosmeticItem,
	changedSlots,
	ROOM_CHARACTER_TYPE,
	type RoomLook,
} from '@app/features/cosmetics/state/Cosmetics';
import {cosmeticName} from '@app/features/cosmetics/utils/CosmeticText';
import {isRoomPartType} from '@app/features/cosmetics/utils/RoomOutfitModels';
import {
	GET_PREMIUM_DESCRIPTOR,
	SOMETHING_WENT_WRONG_DESCRIPTOR,
	TRY_AGAIN_DESCRIPTOR,
	TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR,
} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {ComponentBus} from '@app/features/platform/utils/ComponentBus';
import {shouldShowPremiumFeatures} from '@app/features/premium/utils/PremiumUtils';
import ShopCatalog from '@app/features/shop/state/ShopCatalog';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Spinner} from '@app/features/ui/components/Spinner';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {WarningIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const LOCKED_DESCRIPTOR = msg({
	message: '{name} comes with {premiumProductName}.',
	comment:
		'Profile decorations settings: shown when a Plus-only decoration is picked without Plus. {name} is the decoration name.',
});
const SAVE_DESCRIPTOR = msg({message: 'Save changes', comment: '3D room outfit editor: save the selected outfit.'});
const RESET_DESCRIPTOR = msg({message: 'Reset', comment: '3D room outfit editor: discard the unsaved outfit.'});

function wornLook(): RoomLook {
	return {characterId: Cosmetics.roomCharacterId, outfit: Cosmetics.roomOutfit, shirt: Cosmetics.roomCharacterShirt};
}

function roomLookChanged(draft: RoomLook | null): draft is RoomLook {
	return (
		draft !== null &&
		(draft.characterId !== Cosmetics.roomCharacterId ||
			draft.shirt !== Cosmetics.roomCharacterShirt ||
			Object.keys(changedSlots(draft.outfit, Cosmetics.roomOutfit)).length > 0)
	);
}

// A part the account does not own opens its shop dialog when the shop sells it; anything else explains its lock.
function openRoomShopItem(item: CosmeticItem, explain: (item: CosmeticItem) => void): void {
	const entry = ShopCatalog.entryForSku(`cosmetic:${item.id}`);
	if (entry?.kind !== 'item' || entry.price === null) {
		explain(item);
		return;
	}
	void import('@app/features/shop/components/modals/ShopItemDetailModal').then(({ShopItemDetailModal}) =>
		ModalCommands.pushWithKey(
			ModalCommands.modal(() => (
				<ShopItemDetailModal entry={entry} data-flx="cosmetics.room-outfit-editor-form.shop-item-detail-modal" />
			)),
			`shop-item-${item.id}`,
		),
	);
}

function openPremiumTab(): void {
	ComponentBus.dispatch('USER_SETTINGS_TAB_SELECT', {tab: 'plutonium'});
}

function openPremium(): void {
	void import('@app/features/premium/commands/PremiumModalCommands').then((commands) => commands.open());
}

export const LockedNotice = observer(function LockedNotice({
	item,
	onPremium = openPremiumTab,
}: {
	item: CosmeticItem;
	onPremium?: () => void;
}) {
	const {i18n} = useLingui();
	return (
		<div className={editorStyles.lockedNotice} data-flx="cosmetics.cosmetics-settings-tab.locked-notice">
			<span className={editorStyles.lockedText} data-flx="cosmetics.cosmetics-settings-tab.locked-text">
				{i18n._(LOCKED_DESCRIPTOR, {name: cosmeticName(item, i18n.locale), premiumProductName: PREMIUM_PRODUCT_NAME})}
			</span>
			{shouldShowPremiumFeatures() && (
				<Button small={true} onClick={onPremium} data-flx="cosmetics.cosmetics-settings-tab.button.get-premium">
					{i18n._(GET_PREMIUM_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})}
				</Button>
			)}
		</div>
	);
});

export interface RoomOutfitEditorFormState {
	hasChanges: boolean;
	saving: boolean;
	// Settings resets only after both the room and profile saves succeed.
	save: () => Promise<void>;
	reset: () => void;
}

interface RoomOutfitEditorFormProps {
	// An outer unsaved changes bar owns submission when present.
	onStateChange?: (state: RoomOutfitEditorFormState) => void;
	// Settings shares one lock notice across the profile and room pickers.
	lockState?: {item: CosmeticItem | null; onChange: (item: CosmeticItem | null) => void};
}

export const RoomOutfitEditorForm = observer(function RoomOutfitEditorForm({
	onStateChange,
	lockState,
}: RoomOutfitEditorFormProps) {
	const {i18n} = useLingui();
	const [draft, setDraft] = useState<RoomLook | null>(null);
	const [localLockedItem, setLocalLockedItem] = useState<CosmeticItem | null>(null);
	const lockedItem = lockState ? lockState.item : localLockedItem;
	const setLockedItem = lockState?.onChange ?? setLocalLockedItem;
	const [saving, setSaving] = useState(false);
	const [failed, setFailed] = useState(false);
	const submitting = useRef(false);
	useEffect(() => {
		if (!onStateChange) void Cosmetics.load();
		void ShopCatalog.load();
	}, [onStateChange]);
	const hasChanges = roomLookChanged(draft);
	const reset = useCallback(() => {
		setDraft(null);
		setFailed(false);
	}, []);
	const save = useCallback(async () => {
		if (!roomLookChanged(draft) || submitting.current) return;
		submitting.current = true;
		setSaving(true);
		setFailed(false);
		try {
			await Cosmetics.saveRoomLook(draft);
		} catch (error) {
			setFailed(true);
			throw error;
		} finally {
			submitting.current = false;
			setSaving(false);
		}
	}, [draft]);
	const state = useMemo(() => ({hasChanges, saving, save, reset}), [hasChanges, saving, save, reset]);
	useEffect(() => onStateChange?.(state), [onStateChange, state]);
	const submit = async () => {
		try {
			await save();
			reset();
		} catch {
			// The shared save keeps the draft and exposes the failure for retry.
		}
	};
	const user = Users.currentUser;
	if (Cosmetics.failed) {
		return (
			<StatusSlate
				Icon={WarningIcon}
				title={i18n._(SOMETHING_WENT_WRONG_DESCRIPTOR)}
				description={i18n._(TRY_AGAIN_IN_A_MOMENT_DESCRIPTOR)}
				actions={[{text: i18n._(TRY_AGAIN_DESCRIPTOR), onClick: () => void Cosmetics.load()}]}
				data-flx="cosmetics.room-outfit-editor-form.status-slate"
			/>
		);
	}
	if (!Cosmetics.ready || !user) {
		return (
			<div className={styles.spinnerContainer} data-flx="cosmetics.room-outfit-editor-form.spinner-container">
				<Spinner data-flx="cosmetics.room-outfit-editor-form.spinner" />
			</div>
		);
	}
	// Locked parts show only when owned, included in premium or sold: a part no one can get stays out.
	const sold = new Set(ShopCatalog.itemEntries.flatMap((entry) => (entry.price ? [entry.item.id] : [])));
	const obtainable = (item: CosmeticItem) => item.owned || item.availability !== 'owned' || sold.has(item.id);
	const parts = Cosmetics.roomParts.filter(obtainable);
	const characters = Cosmetics.items.filter((item) => item.type === ROOM_CHARACTER_TYPE && obtainable(item));
	return (
		<div data-flx="cosmetics.room-outfit-editor-form.form">
			<RoomOutfitEditor
				look={draft ?? wornLook()}
				parts={parts}
				characters={characters}
				userId={user.id}
				isPremium={user.isPremium()}
				onChange={(look) => {
					setLockedItem(null);
					setFailed(false);
					setDraft(look);
				}}
				onLocked={(item) => openRoomShopItem(item, setLockedItem)}
				data-flx="cosmetics.room-outfit-editor-form.editor"
			/>
			{lockedItem && (lockedItem.type === ROOM_CHARACTER_TYPE || isRoomPartType(lockedItem.type)) && (
				<LockedNotice
					item={lockedItem}
					onPremium={onStateChange ? undefined : openPremium}
					data-flx="cosmetics.room-outfit-editor-form.locked-notice"
				/>
			)}
			{!onStateChange && (
				<>
					{failed && (
						<p role="alert" data-flx="cosmetics.room-outfit-editor-form.error">
							{i18n._(SOMETHING_WENT_WRONG_DESCRIPTOR)}
						</p>
					)}
					<div className={editorStyles.actions} data-flx="cosmetics.room-outfit-editor-form.actions">
						<Button onClick={reset} disabled={!hasChanges || saving} data-flx="cosmetics.room-outfit-editor-form.reset">
							{i18n._(RESET_DESCRIPTOR)}
						</Button>
						<Button
							onClick={() => void submit()}
							disabled={!hasChanges || saving}
							submitting={saving}
							data-flx="cosmetics.room-outfit-editor-form.save"
						>
							{i18n._(SAVE_DESCRIPTOR)}
						</Button>
					</div>
				</>
			)}
		</div>
	);
});
